/**
 * One SOP (plan docs/plans/lms-plan.md 11.1, 11.2, 11.3; Appendix A GET
 * /sops/:id; 18.25, 18.27; WP-46), at /lms/sops?sop=<id>.
 *
 * For an lms admin, the version tracker (11.1): every version, newest first,
 * with its status, dates, uploader, sha256, the linked training version and
 * campaign and the acknowledgement counts, and the admin actions: upload a
 * version, make a draft effective (a confirm dialog: the effective version is
 * superseded and stays downloadable), send for training (the four steps of
 * 11.2), the acknowledgement tracker per version (11.3), edit, and retire.
 * For a learner, the versions they may open (the effective one, and any they
 * train on or hold a record of) and their own training.
 *
 * The page keeps showing the SOP while it reloads after an action, so an open
 * dialog stays put.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Loader2, Pencil, Upload } from 'lucide-react';
import ConfirmModal from '../../../ui/ConfirmModal';
import { outlineBtnCls, primaryBtnCls, secondaryBtnCls } from '../../../ui/styles';
import { useToast } from '../../../ui/Toast';
import { useLms } from '../../LmsProvider';
import { apiErrorMessage } from '../../api/client';
import { getSop, getSopVersionUrl, makeSopVersionEffective } from '../../api/sops';
import type { SendSopForTrainingResult, SopDetail, SopDetailVersion } from '../../api/sops';
import { Pill } from '../../components/StatusPill';
import {
  cardCls,
  ErrorNote,
  formatDay,
  linkBtnCls,
  metaCls,
  microLabelCls,
  sectionTitleCls,
  tableCardCls,
  tableCls,
  tbodyCls,
  tdCls,
  thCls,
  theadCls,
} from '../../components/pickers/shared';
import { catalogPaths, POLICY_TEXT } from '../manage/catalog/model';
import { AckTracker } from './AckTracker';
import { fileKind, fileSize, formatSopDay, SOP_STATUS_LABELS, SOPS_PATH, sendStateOf, shortHash, VERSION_STATUS_LABELS, VERSION_STATUS_TONES } from './labels';
import { OpenFileButton } from './OpenFileButton';
import { RetireSopModal } from './RetireSopModal';
import { SendForTrainingModal } from './SendForTrainingModal';
import type { SendKind } from './sendForTraining';
import { SopFormModal } from './SopFormModal';
import { UploadVersionModal } from './UploadVersionModal';

export interface SopDetailViewProps {
  sopId: string;
  isAdmin: boolean;
  onOpenSop: (sopId: string) => void;
}

/** The Assign wizard (8.2): more people for an SOP already sent come from there, kind sop_update (18.25). */
const ASSIGN_PATH = '/lms/manage/assign';

function VersionStatusPill({ status }: { status: SopDetailVersion['status'] }) {
  return (
    <Pill tone={VERSION_STATUS_TONES[status] ?? 'gray'} data-testid={`lms-sop-version-status-${status}`}>
      {VERSION_STATUS_LABELS[status] ?? status}
    </Pill>
  );
}

function versionDates(v: SopDetailVersion): string[] {
  const out: string[] = [];
  if (v.effective_on) out.push(`Effective on ${formatSopDay(v.effective_on)}`);
  if (v.made_effective_at) out.push(`Made effective ${formatDay(v.made_effective_at)}${v.made_effective_by_name ? ` by ${v.made_effective_by_name}` : ''}`);
  if (v.superseded_at) out.push(`Superseded ${formatDay(v.superseded_at)}`);
  if (v.retired_at) out.push(`Retired ${formatDay(v.retired_at)}`);
  return out;
}

export function SopDetailView({ sopId, isAdmin, onOpenSop }: SopDetailViewProps) {
  const { siteId } = useLms();
  const { toast } = useToast();
  const [detail, setDetail] = useState<SopDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const request = useRef(0);

  const [editing, setEditing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [retiring, setRetiring] = useState(false);
  const [confirmEffective, setConfirmEffective] = useState<SopDetailVersion | null>(null);
  const [makingEffective, setMakingEffective] = useState(false);
  const [sendTarget, setSendTarget] = useState<{ version: SopDetailVersion; kind: SendKind } | null>(null);
  const [ackVersion, setAckVersion] = useState<SopDetailVersion | null>(null);

  useEffect(() => {
    const id = ++request.current;
    setLoading(true);
    setError(null);
    getSop(sopId)
      .then((answer) => {
        if (id !== request.current) return;
        setDetail({ sop: answer.sop, versions: Array.isArray(answer?.versions) ? answer.versions : [] });
      })
      .catch((err) => {
        if (id !== request.current) return;
        setDetail(null);
        const status = (err as { response?: { status?: number } } | null)?.response?.status;
        setError(status === 404 ? 'This SOP was not found.' : apiErrorMessage(err, 'The SOP could not be loaded.'));
      })
      .finally(() => {
        if (id === request.current) setLoading(false);
      });
  }, [sopId, siteId, attempt]);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);

  const makeEffective = async () => {
    if (!confirmEffective || makingEffective) return;
    setMakingEffective(true);
    try {
      await makeSopVersionEffective(sopId, confirmEffective.id);
      toast(`Version ${confirmEffective.version_label} is now effective.`, { type: 'success' });
      setConfirmEffective(null);
      reload();
    } catch (err) {
      toast(apiErrorMessage(err, 'The version could not be made effective.'), { type: 'error' });
    } finally {
      setMakingEffective(false);
    }
  };

  const back = (
    <Link to={SOPS_PATH} className={linkBtnCls} data-testid="lms-sop-back">
      <ArrowLeft className="h-4 w-4" aria-hidden="true" />
      All SOPs
    </Link>
  );

  if (!detail) {
    return (
      <div className="space-y-3" data-testid="lms-sop-detail">
        {back}
        {loading && (
          <div className="flex justify-center py-10">
            <Loader2 className="h-5 w-5 animate-spin text-primary-600 dark:text-primary-400" aria-hidden="true" />
            <span className="sr-only">Loading the SOP</span>
          </div>
        )}
        {error && (
          <ErrorNote testId="lms-sop-detail-error">
            {error}{' '}
            <button type="button" className="font-medium underline" onClick={reload}>
              Try again
            </button>
          </ErrorNote>
        )}
      </div>
    );
  }

  const { sop, versions } = detail;
  const active = sop.status === 'active';
  const my = sop.my_training;
  const effective = versions.find((v) => v.status === 'effective') || null;

  return (
    <div className="space-y-4" data-testid="lms-sop-detail">
      {back}

      <section className={`${cardCls} space-y-3`} aria-labelledby="lms-sop-heading">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 space-y-1">
            <div className={microLabelCls}>{`SOP ${sop.sop_number}`}</div>
            <h2 id="lms-sop-heading" className="text-lg font-semibold leading-6 text-gray-900 dark:text-gray-100" data-testid="lms-sop-heading">
              {sop.title}
            </h2>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-600 dark:text-gray-300">
              <Pill tone={active ? 'green' : 'gray'} data-testid="lms-sop-status">{SOP_STATUS_LABELS[sop.status] ?? sop.status}</Pill>
              {sop.category && <span>{sop.category}</span>}
              <span>{`Owner: ${sop.owner?.full_name || 'none named'}`}</span>
              {effective && <span>{`Effective version ${effective.version_label}`}</span>}
            </div>
            {sop.course && (
              <p className={metaCls}>
                {'Training: '}
                <Link to={`/lms/courses/${encodeURIComponent(sop.course.id)}`} className="font-medium text-primary-700 hover:underline dark:text-primary-400">
                  {sop.course.title}
                </Link>
                {` (${sop.course.kind === 'quiz' ? 'quiz' : 'read and acknowledge'}${sop.course.is_active ? '' : ', retired'})`}
                {isAdmin && (
                  <>
                    {' \u00b7 '}
                    <Link to={catalogPaths.course(sop.course.id)} className="font-medium text-primary-700 hover:underline dark:text-primary-400">
                      Open in the catalog
                    </Link>
                  </>
                )}
              </p>
            )}
          </div>
          {isAdmin && active && (
            <div className="flex flex-wrap items-center gap-2">
              <button type="button" className={primaryBtnCls} onClick={() => setUploading(true)} data-testid="lms-sop-upload">
                <Upload className="h-4 w-4" aria-hidden="true" />
                Upload version
              </button>
              <button type="button" className={secondaryBtnCls} onClick={() => setEditing(true)} data-testid="lms-sop-edit">
                <Pencil className="h-4 w-4" aria-hidden="true" />
                Edit
              </button>
              <button
                type="button"
                className={`${outlineBtnCls()} text-red-700 dark:text-red-400`}
                onClick={() => setRetiring(true)}
                data-testid="lms-sop-retire"
              >
                Retire
              </button>
            </div>
          )}
        </div>

        {my && (my.assignment || my.acknowledged_at) && (
          <div className="flex flex-wrap items-center gap-2 border-t border-gray-100 pt-3 dark:border-gray-700" data-testid="lms-sop-my-training">
            <span className={microLabelCls}>Your training</span>
            {my.assignment ? (
              <>
                {Date.parse(my.assignment.due_at) < Date.now() ? (
                  <Pill tone="red">Overdue</Pill>
                ) : (
                  <Pill tone="primary">{`Due ${formatDay(my.assignment.due_at)}`}</Pill>
                )}
                {sop.course_id && (
                  <Link to={`/lms/courses/${encodeURIComponent(sop.course_id)}/learn`} className={linkBtnCls}>
                    Start the training
                  </Link>
                )}
              </>
            ) : (
              <Pill tone="green">{`Acknowledged ${formatDay(my.acknowledged_at)}`}</Pill>
            )}
          </div>
        )}
      </section>

      <section className="space-y-2" aria-labelledby="lms-sop-versions">
        <div className="flex items-center gap-2">
          <h3 id="lms-sop-versions" className={sectionTitleCls}>
            {isAdmin ? 'Version tracker' : 'Versions'}
          </h3>
          {loading && <Loader2 className="h-4 w-4 animate-spin text-primary-600 dark:text-primary-400" aria-label="Refreshing" />}
        </div>
        {error && <ErrorNote testId="lms-sop-detail-error">{error}</ErrorNote>}
        {versions.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400" data-testid="lms-sop-no-versions">
            {isAdmin ? 'No version is uploaded yet. Upload the first one as a draft, then make it effective.' : 'There is no version of this SOP you can open.'}
          </p>
        ) : (
          <div className={tableCardCls}>
            <table className={tableCls} data-testid="lms-sop-versions">
              <thead className={theadCls}>
                <tr>
                  <th scope="col" className={thCls}>Version</th>
                  <th scope="col" className={thCls}>Dates</th>
                  <th scope="col" className={thCls}>File</th>
                  {isAdmin && <th scope="col" className={thCls}>Training</th>}
                  <th scope="col" className={thCls}>
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className={tbodyCls}>
                {versions.map((v) => {
                  const state = isAdmin ? sendStateOf(sop, v) : 'none';
                  const dates = versionDates(v);
                  return (
                    <tr key={v.id} data-testid="lms-sop-version-row" data-version-id={v.id}>
                      <td className={`${tdCls} align-top`}>
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-medium tabular-nums">{v.version_label}</span>
                          <VersionStatusPill status={v.status} />
                        </div>
                        {v.change_summary && (
                          <p className={`${metaCls} mt-1 max-w-xs whitespace-pre-line`}>{v.change_summary}</p>
                        )}
                      </td>
                      <td className={`${tdCls} align-top`}>
                        <div className={metaCls}>
                          {`Uploaded ${formatDay(v.uploaded_at)}${v.uploaded_by_name ? ` by ${v.uploaded_by_name}` : ''}`}
                        </div>
                        {dates.map((d) => (
                          <div key={d} className={metaCls}>
                            {d}
                          </div>
                        ))}
                      </td>
                      <td className={`${tdCls} align-top`}>
                        <div className="max-w-[16rem] truncate" title={v.filename}>
                          {v.filename}
                        </div>
                        <div className={metaCls}>{`${fileKind(v.mime)} \u00b7 ${fileSize(v.size_bytes)}`}</div>
                        {isAdmin && v.sha256 && (
                          <div className={`${metaCls} font-mono`} title={`SHA-256 ${v.sha256}`}>
                            {`SHA-256 ${shortHash(v.sha256)}`}
                          </div>
                        )}
                      </td>
                      {isAdmin && (
                        <td className={`${tdCls} align-top`} data-testid="lms-sop-version-training">
                          {v.course_version ? (
                            <div className={metaCls}>
                              {`Training version ${v.course_version.version_no}, ${v.course_version.status}`}
                              {v.course_version.retrain_policy && v.course_version.status !== 'draft' && POLICY_TEXT[v.course_version.retrain_policy]
                                ? ` (${POLICY_TEXT[v.course_version.retrain_policy].label})`
                                : ''}
                            </div>
                          ) : (
                            <div className={metaCls}>Not sent for training</div>
                          )}
                          {v.campaign && (
                            <div className={metaCls}>{`Sent ${formatDay(v.campaign.sent_at)} to ${v.campaign.recipient_count.toLocaleString()}`}</div>
                          )}
                          {v.training && (
                            <div className="text-sm text-gray-900 dark:text-gray-100" data-testid="lms-sop-version-counts">
                              {`${v.training.acknowledged.toLocaleString()} acknowledged \u00b7 ${v.training.pending.toLocaleString()} pending`}
                            </div>
                          )}
                        </td>
                      )}
                      <td className={`${tdCls} align-top`}>
                        <div className="flex flex-wrap items-center justify-end gap-2">
                          <OpenFileButton
                            resolve={() => getSopVersionUrl(sop.id, v.id)}
                            label="Open"
                            ariaLabel={`Open version ${v.version_label}`}
                            testId="lms-sop-version-open"
                            failureMessage="The file could not be opened. Try again."
                          />
                          {isAdmin && active && v.status === 'draft' && (
                            <button type="button" className={secondaryBtnCls} onClick={() => setConfirmEffective(v)} data-testid="lms-sop-make-effective">
                              Make effective
                            </button>
                          )}
                          {state === 'effective_first' && (
                            <span className={`${metaCls} w-full text-right`} data-testid="lms-sop-effective-first">
                              Make it effective, then send it for training.
                            </span>
                          )}
                          {state === 'send' && (
                            <button
                              type="button"
                              className={primaryBtnCls}
                              onClick={() => setSendTarget({ version: v, kind: 'send' })}
                              data-testid="lms-sop-send-open"
                            >
                              Send for training
                            </button>
                          )}
                          {state === 'send_people' && (
                            <button
                              type="button"
                              className={primaryBtnCls}
                              onClick={() => setSendTarget({ version: v, kind: 'send_people' })}
                              data-testid="lms-sop-send-people"
                            >
                              Send to people
                            </button>
                          )}
                          {state === 'finish_quiz' && v.course_version && (
                            <Link to={catalogPaths.version(v.course_version.id)} className={secondaryBtnCls} data-testid="lms-sop-finish-quiz">
                              Finish the quiz
                            </Link>
                          )}
                          {state === 'sent' && (
                            <Link to={ASSIGN_PATH} className={linkBtnCls} data-testid="lms-sop-assign-more">
                              Assign more people
                            </Link>
                          )}
                          {isAdmin && v.course_version_id && (
                            <button
                              type="button"
                              className={outlineBtnCls(ackVersion?.id === v.id)}
                              onClick={() => setAckVersion(ackVersion?.id === v.id ? null : v)}
                              aria-pressed={ackVersion?.id === v.id}
                              data-testid="lms-sop-ack-open"
                            >
                              Acknowledgements
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {isAdmin && ackVersion && (
        <AckTracker key={ackVersion.id} sopId={sop.id} versionId={ackVersion.id} versionLabel={ackVersion.version_label} onClose={() => setAckVersion(null)} />
      )}

      {isAdmin && (
        <>
          <SopFormModal
            mode="edit"
            isOpen={editing}
            sop={sop}
            onClose={() => setEditing(false)}
            onSaved={() => {
              setEditing(false);
              toast('SOP saved.', { type: 'success' });
              reload();
            }}
            onOpenExisting={onOpenSop}
          />
          <UploadVersionModal
            isOpen={uploading}
            sopId={sop.id}
            sopNumber={sop.sop_number}
            hasVersions={versions.length > 0}
            onClose={() => setUploading(false)}
            onUploaded={(v) => {
              setUploading(false);
              toast(`Version ${v.version_label} uploaded as a draft.`, { type: 'success' });
              reload();
            }}
          />
          <RetireSopModal
            isOpen={retiring}
            sopId={sop.id}
            sopNumber={sop.sop_number}
            sopTitle={sop.title}
            hasCourse={Boolean(sop.course)}
            onClose={() => setRetiring(false)}
            onRetired={() => {
              setRetiring(false);
              toast(`SOP ${sop.sop_number} retired.`, { type: 'success' });
              reload();
            }}
          />
          <ConfirmModal
            isOpen={confirmEffective !== null}
            onClose={() => (makingEffective ? undefined : setConfirmEffective(null))}
            onConfirm={makeEffective}
            loading={makingEffective}
            title={confirmEffective ? `Make version ${confirmEffective.version_label} effective?` : 'Make effective?'}
            message={
              effective
                ? `Version ${effective.version_label} stops being effective and is marked superseded. It stays on record and downloadable from this tracker.`
                : 'It becomes the version everyone opens from the SOP library.'
            }
            confirmLabel="Make effective"
          />
          {sendTarget && (
            <SendForTrainingModal
              isOpen
              sop={sop}
              version={sendTarget.version}
              kind={sendTarget.kind}
              onClose={() => setSendTarget(null)}
              onSent={(out: SendSopForTrainingResult) => {
                toast(
                  out.campaign ? `SOP ${sop.sop_number} sent for training.` : `The quiz draft for SOP ${sop.sop_number} is ready in the course editor.`,
                  { type: 'success' }
                );
                reload();
              }}
            />
          )}
        </>
      )}
    </div>
  );
}

export default SopDetailView;
